import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../../redux/store";
import { Col, Form, Row } from "react-bootstrap";
import { SurveyFormDto, SurveySelectorsDataDto } from '../../../types';
import { getSelectorsData } from "../../../api/surveyAPI";
import  { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { InputDatePicker } from "../../../../../../components/InputDatePicker";
import { setSurveyForm } from "../../../../../../redux/surveySlice";
import { SelectItemDto } from "../../../../../../types/SelectItemDto";
import { SurveyStatusEnum } from "../../../types/enum";

export default function SurveyDefinitionForm() {
    const dispatch = useDispatch<AppDispatch>();
    const survey = useSelector((state: RootState) => state.surveyForm.data);
    const errors = useSelector((state: RootState) => state.surveyForm.errors);
    const { t } = useTranslation();

    const updateField = (field: keyof SurveyFormDto, value: any) => {
        dispatch(setSurveyForm({ [field]: value }));
    };

    const [campaigns, setCampaigns] = useState<SelectItemDto[]>([]);
    const [functionalAreas, setFunctionalAreas] = useState<SelectItemDto[]>([]);

    useEffect(() => {
        getSelectorsData().then((selectorsData: SurveySelectorsDataDto) => {
            setCampaigns(selectorsData.campaings);
            setFunctionalAreas(selectorsData.functionalAreas);
        }).catch(() => {
            setCampaigns([]);
            setFunctionalAreas([]);
        });
    }, []);

    return (
        <div>
            <Row className="mb-4">
                <Col xs={12}>
                    <h3>{t("surveyAdmin:form.dataTitle")}</h3>
                </Col>
            </Row>

            <Form>
                <Row>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:form.surveyIdField")}</Form.Label>
                            <Form.Control
                                value={survey.id ?? ""}
                                disabled
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("common:status")}</Form.Label>
                            <Form.Select
                                as="select"
                                value={survey.status ?? 0}
                                onChange={(e) => updateField("status", e.target.value)}
                            >
                                <option value=""></option>
                                {Object.values(SurveyStatusEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                    const value = SurveyStatusEnum[name as keyof typeof SurveyStatusEnum];
                                    return (<option key={value} value={value}>
                                        {t(`surveyAdmin:status.${name}`)} 
                                    </option>)
                                })}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={8}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:form.surveyNameField")}</Form.Label>
                            <Form.Control
                                value={survey.surveyName ?? ""}
                                onChange={(e) => updateField("surveyName", e.target.value)}
                                isInvalid={!!errors?.surveyName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors?.surveyName}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={8}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:form.surveyCampaignField")}</Form.Label>
                            <Form.Select
                                as="select"
                                value={survey.campaignId ?? ""}
                                isInvalid={!!errors?.campaignId}
                                onChange={(e) => {
                                    updateField("campaignId", e.target.value);

                                    const campaignName = campaigns.find((campaign) => campaign.id === Number(e.target.value))?.name ?? "";
                                    updateField("campaignName", campaignName);
                                }}
                            >
                                <option value=""></option>
                                {campaigns.map((campaign, index) => <option key={index} value={campaign.id}>{campaign.name}</option>)}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors?.campaignId}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                            <InputDatePicker
                                value={survey.startDate ? new Date(survey.startDate) : null}
                                onChange={(value) => updateField("startDate", value)}
                                label={t("surveyAdmin:form.surveyStartDateField")}
                                hasError={'startDate' in errors && !!errors.startDate}
                                errorMessage={errors?.startDate}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                            <InputDatePicker
                                value={survey.endDate ? new Date(survey.endDate) : null}
                                onChange={(value) => updateField("endDate", value)}
                                label={t("surveyAdmin:form.surveyEndDateField")}
                                hasError={'endDate' in errors && !!errors.endDate}
                                errorMessage={errors?.endDate}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={8}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:form.surveyFunctionalAreaField")}</Form.Label>
                            <Form.Select
                                as="select"
                                value={survey.functionalAreaId ?? ""}
                                onChange={(e) => updateField("functionalAreaId", e.target.value)}
                            >
                                <option value=""></option>
                                {functionalAreas.map((functionalArea) => (
                                    <option key={functionalArea.id} value={functionalArea.id}>{functionalArea.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={6} md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:form.surveySending")}</Form.Label>
                            <Form.Check
                                type="checkbox"
                                label={t("surveyAdmin:form.sendBySMS")}
                                checked={survey.sendBySMS}
                                onChange={(e) => { updateField('sendBySMS', e.target.checked) }}
                            />
                            <Form.Check
                                type="checkbox"
                                label={t("surveyAdmin:form.sendByEmail")}
                                checked={survey.sendByEmail}
                                onChange={(e) => { updateField('sendByEmail', e.target.checked) }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}
